/**
 * @file wms-period.ts
 * @description Селект «Период» в журналах складских документов
 * (phase19.23 этап B).
 *
 * Зачем отдельный файл: журналов три (приёмки, списания, перемещения), а
 * правило одно — «последние N дней от текущего момента». Варианты держим
 * реестром, а не тремя литералами по месту: добавить «год» = запись в
 * `WMS_PERIOD_OPTIONS`, экраны не меняются
 * (docs/processes/engineering-principles.md, OCP).
 *
 * Верхнюю границу (`to`) не шлём осознанно: документ, созданный секунду назад,
 * обязан попасть в выдачу, а фиксированный `to` этого не гарантирует (часы
 * клиента и сервера расходятся). Нижнюю границу считаем ISO-датой-временем —
 * форма `2026-09-01` у сервера раскрывается до начала суток, и «30 дней» стали
 * бы «30 дней и ещё кусок».
 */

/** Значение селекта: строка, потому что `SelectField` работает со строками. */
export type WmsPeriodValue = "30" | "90" | "all";

/** Период по умолчанию: журнал перестановок за месяц читается, за год — нет. */
export const WMS_PERIOD_DEFAULT: WmsPeriodValue = "30";

/** Варианты селекта — реестр, а не ветки по месту отрисовки. */
export const WMS_PERIOD_OPTIONS: { value: WmsPeriodValue; label: string }[] = [
  { value: "30", label: "30 дней" },
  { value: "90", label: "90 дней" },
  { value: "all", label: "Всё время" },
];

/** Сколько дней назад отсчитывать; `all` — границы нет. */
const PERIOD_DAYS: Record<WmsPeriodValue, number | null> = {
  "30": 30,
  "90": 90,
  all: null,
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Нижняя граница периода для query.
 *
 * @param period значение селекта;
 * @param now    точка отсчёта (в тестах — фиксированная дата).
 * @returns ISO-строка или `undefined` для «всё время».
 */
export function wmsPeriodFrom(
  period: WmsPeriodValue | string,
  now: Date = new Date(),
): string | undefined {
  const days = PERIOD_DAYS[period as WmsPeriodValue];
  if (!days) return undefined;
  return new Date(now.getTime() - days * DAY_MS).toISOString();
}

/** Подпись периода для пустого состояния: «за последние 30 дней». */
export function wmsPeriodLabel(period: WmsPeriodValue | string): string {
  const option = WMS_PERIOD_OPTIONS.find((item) => item.value === period);
  return option ? option.label.toLowerCase() : "выбранный период";
}
